import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeploymentDestroyMutation, useDeploymentListQuery } from '../api/deployment.js';
import { formatDateUTC, timeToNow } from '../components/utils.js';

type DeploymentListParams = {
  projectId: string;
  testId: string;
};

export function DeploymentList({ projectId, testId }: DeploymentListParams) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: deployments,
    isLoading,
    isError,
    error
  } = useDeploymentListQuery(projectId, testId, {
    limit: rowsPerPage,
    offset: page * rowsPerPage
  });
  const deploymentDestroy = useDeploymentDestroyMutation(projectId, testId);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (deploymentId: string) => {
    deploymentDestroy.mutate(
      { deploymentId },
      {
        onSuccess: () => enqueueSnackbar('Deployment deleted', { variant: 'success' }),
        onError: (err) => enqueueSnackbar(`Failed to delete deployment: ${err.message}`, { variant: 'error' })
      }
    );
  };

  const deletingDeploymentId = deploymentDestroy.variables?.deploymentId;

  if (isLoading) return <CircularProgress />;
  if (isError) return <Alert severity="error">{`Error: ${error.message}`}</Alert>;

  return (
    <TableContainer>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Snapshot</TableCell>
            <TableCell>Date (UTC)</TableCell>
            <TableCell>Timestamp</TableCell>
            <TableCell>Signature</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deployments.map((deployment) => (
            <TableRow key={deployment.id}>
              <TableCell>{deployment.id}</TableCell>
              <TableCell>
                <Link to={`/projects/${projectId}/snapshots/web/${deployment.snapshotId}`}>
                  {deployment.snapshotId}
                </Link>
              </TableCell>
              <TableCell>{formatDateUTC(deployment.createdDate)}</TableCell>
              <TableCell>{timeToNow(deployment.createdDate)}</TableCell>
              <TableCell>{deployment.signature}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label="Delete deployment"
                  onClick={(event) => {
                    event.preventDefault();
                    handleDelete(deployment.id);
                  }}
                  disabled={deploymentDestroy.isLoading && deletingDeploymentId === deployment.id}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              count={-1}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton={true}
              showLastButton={true}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
